<template>
  <div>
    <div class="row mb-4">
      <div class="col-md-2 mb-1">
        <label>ID do Pedido</label>
        <input type="text" class="form-control" v-model="filtro.id" />
      </div>
      <div class="col-md-3 mb-1">
        <label>Unidade Solicitante</label>
        <v-select
          v-model="filtro.unidade_solicitante"
          class="vselect"
          :options="unidades"
          :reduce="(unidades) => unidades.id"
          :label="'name'"
        />
      </div>
      <div class="col-md-2 mb-1">
        <label>Data de</label>
        <input type="date" class="form-control" v-model="filtro.data_de" />
      </div>
      <div class="col-md-2 mb-1">
        <label>Data até</label>
        <input type="date" class="form-control" v-model="filtro.data_ate" />
      </div>
      <div class="col-md-3 mb-1">
        <br />
        <button class="btn btn-secondary mr-2" @click="limparFiltros()">
          Limpar
        </button>
        <button
          class="btn btn-success"
          @click="exportarParaExcel()"
          v-if="dadosFiltrados.length > 0"
        >
          Exportar Excel
        </button>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <b-card title="Pesquisa de Material">
          <div class="row">
            <div class="col-md-12 mb-4">
              <label for="materialSelect">Pesquisar Material:</label>
              <v-select
                id="materialSelect"
                v-model="materialSelecionado"
                :options="materiaisFiltrados"
                :label="'nome'"
                :filterable="false"
                :searchable="true"
                placeholder="Digite para buscar um material..."
                @search="onSearch"
                @input="onMaterialSelected"
                :loading="loadingMateriais"
                :reduce="(material) => material"
                class="vselect"
                :clearSearchOnSelect="false"
                :clearable="true"
              >
                <template v-slot:no-options="{ search, searching }">
                  <div
                    v-if="searching && search.length >= 3 && !loadingMateriais"
                  >
                    Nenhum material encontrado para "{{ search }}"
                  </div>
                  <div v-else-if="searching && search.length < 3">
                    Digite pelo menos 3 caracteres para buscar
                  </div>
                  <div v-else-if="loadingMateriais">Buscando materiais...</div>
                  <div v-else>Digite para buscar materiais</div>
                </template>
              </v-select>
            </div>

            <!-- Exibir material selecionado -->
            <div v-if="materialSelecionado" class="col-md-12 mb-3">
              <b-card class="material-info">
                <div
                  class="d-flex justify-content-between align-items-center mb-3"
                >
                  <h5 class="mb-0">Material Selecionado:</h5>
                  <b-button
                    variant="primary"
                    @click="pesquisarUnidades"
                    :disabled="loadingUnidades"
                  >
                    <b-spinner v-if="loadingUnidades" small></b-spinner>
                    {{
                      loadingUnidades ? "Pesquisando..." : "Pesquisar Unidades"
                    }}
                  </b-button>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <strong>Nome:</strong> {{ materialSelecionado.nome }}
                  </div>
                  <div class="col-md-6" v-if="materialSelecionado.codigo">
                    <strong>Código:</strong> {{ materialSelecionado.codigo }}
                  </div>
                  <div class="col-md-6" v-if="materialSelecionado.descricao">
                    <strong>Descrição:</strong>
                    {{ materialSelecionado.descricao }}
                  </div>
                  <div
                    class="col-md-6"
                    v-if="
                      materialSelecionado.grupo &&
                      materialSelecionado.grupo.nome
                    "
                  >
                    <strong>Grupo:</strong> {{ materialSelecionado.grupo.nome }}
                  </div>
                </div>
              </b-card>
            </div>

            <!-- Tabela de Unidades que solicitaram o material -->
            <div v-if="unidadesSolicitantes.length > 0" class="col-md-12">
              <b-card>
                <div
                  class="d-flex justify-content-between align-items-center mb-3"
                >
                  <h5 class="mb-0">
                    Unidades que solicitaram "{{ materialSelecionado.nome }}"
                  </h5>
                  <div class="d-flex align-items-center">
                    <b-badge variant="info" class="mr-3"
                      >{{ dadosFiltrados.length }} de
                      {{ unidadesSolicitantes.length }} registros
                      encontrados</b-badge
                    >
                    <span class="text-muted mr-3" v-if="totalPaginas > 1"
                      >Página {{ paginaAtual }} de {{ totalPaginas }}</span
                    >

                    <!-- Seletor de registros por página -->
                    <div
                      class="d-flex align-items-center"
                      v-if="totalPaginas > 1"
                    >
                      <label class="mr-2 mb-0 text-muted"
                        >Registros por página:</label
                      >
                      <select
                        class="form-control form-control-sm"
                        style="width: auto"
                        v-model="registrosPorPagina"
                      >
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div class="table-responsive">
                  <table class="table table-hover table-dark-responsive">
                    <thead>
                      <tr>
                        <th>ID Pedido</th>
                        <th>Unidade Solicitante</th>
                        <th>Data do Pedido</th>
                        <th>Quantidade Solicitada</th>
                        <th>Quantidade Atendida</th>
                        <th>Baixa de Estoque</th>
                        <th>Status do Pedido</th>
                        <th>Tipo</th>
                        <th class="text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in dadosPaginados"
                        :key="`${item.pedido_id}-${item.material_pedido_id}`"
                      >
                        <td>
                          <strong>#{{ item.pedido_id }}</strong>
                        </td>
                        <td>
                          <strong>{{
                            item.unidade_nome || "Não informado"
                          }}</strong>
                        </td>
                        <td>
                          {{ formatarData(item.data_pedido) }}
                        </td>
                        <td>
                          <b-badge variant="secondary">{{
                            item.quantidade_solicitada
                          }}</b-badge>
                        </td>
                        <td>
                          <b-badge variant="info">{{
                            item.quantidade_atendida || 0
                          }}</b-badge>
                        </td>
                        <td>
                          <b-badge variant="success">{{
                            item.baixa_estoque || 0
                          }}</b-badge>
                        </td>
                        <td>
                          <b-badge :variant="getStatusVariant(item.status)">
                            {{ getStatusText(item.status) }}
                          </b-badge>
                        </td>
                        <td>
                          <span
                            class="badge badge-primary"
                            v-show="item.data_inicio && item.data_fim"
                            >Pedido semanal</span
                          >
                          <span
                            class="badge badge-warning"
                            v-show="
                              !item.data_inicio &&
                              !item.data_fim &&
                              !item.suplementar &&
                              !item.rapido
                            "
                            >Pedido SOS</span
                          >
                          <span
                            class="badge badge-secondary"
                            v-show="
                              !item.data_inicio &&
                              !item.data_fim &&
                              item.suplementar
                            "
                            >Pedido Suplementar</span
                          >
                          <span
                            class="badge badge-info"
                            v-show="
                              !item.data_inicio && !item.data_fim && item.rapido
                            "
                            >Pedido Rápido</span
                          >
                        </td>
                        <td class="text-center">
                          <router-link
                            :to="`/pedidos/view/${item.pedido_id}`"
                            class="btn btn-sm btn-primary btn-ver-pedido"
                            @click.native="
                              console.log(
                                'Clicando pedido:',
                                item.pedido_id,
                                'Status na tabela:',
                                item.status,
                                'Status texto:',
                                getStatusText(item.status)
                              )
                            "
                          >
                            <i class="fas fa-eye"></i> Ver Pedido
                          </router-link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Controles de Paginação -->
                <div
                  v-if="totalPaginas > 1"
                  class="d-flex justify-content-center align-items-center mt-3"
                >
                  <button
                    class="btn btn-sm btn-outline-secondary mr-2"
                    @click="irParaPagina(1)"
                    :disabled="paginaAtual === 1"
                  >
                    <i class="fas fa-angle-double-left"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-secondary mr-2"
                    @click="irParaPagina(paginaAtual - 1)"
                    :disabled="paginaAtual === 1"
                  >
                    <i class="fas fa-angle-left"></i> Anterior
                  </button>

                  <span class="mx-3"
                    >Página {{ paginaAtual }} de {{ totalPaginas }}</span
                  >

                  <button
                    class="btn btn-sm btn-outline-secondary mr-2"
                    @click="irParaPagina(paginaAtual + 1)"
                    :disabled="paginaAtual === totalPaginas"
                  >
                    Próximo <i class="fas fa-angle-right"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    @click="irParaPagina(totalPaginas)"
                    :disabled="paginaAtual === totalPaginas"
                  >
                    <i class="fas fa-angle-double-right"></i>
                  </button>
                </div>
              </b-card>
            </div>

            <!-- Mensagem quando não há resultados -->
            <div
              v-else-if="pesquisaRealizada && unidadesSolicitantes.length === 0"
              class="col-md-12"
            >
              <b-alert show variant="info" class="text-center">
                <i class="fas fa-info-circle"></i>
                Nenhuma unidade solicitou o material "{{
                  materialSelecionado.nome
                }}" ainda.
              </b-alert>
            </div>
          </div>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BButton,
  BSpinner,
  BBadge,
  BAlert,
} from "bootstrap-vue";
import vSelect from "vue-select";

export default {
  name: "PesquisaMaterialCrud",
  components: {
    BCard,
    BCardText,
    BButton,
    BSpinner,
    BBadge,
    BAlert,
    vSelect,
  },
  data() {
    return {
      materialSelecionado: null,
      materiaisFiltrados: [],
      loadingMateriais: false,
      searchTimeout: null,
      unidadesSolicitantes: [],
      loadingUnidades: false,
      pesquisaRealizada: false,
      filtro: [],
      unidades: [],
      // Controles de paginação
      paginaAtual: 1,
      registrosPorPagina: 25,
    };
  },
  computed: {
    dadosFiltrados() {
      let dados = this.unidadesSolicitantes;

      // Filtro por ID do pedido
      if (this.filtro.id) {
        dados = dados.filter((item) =>
          item.pedido_id.toString().includes(this.filtro.id.toString())
        );
      }

      // Filtro por unidade solicitante
      if (this.filtro.unidade_solicitante) {
        dados = dados.filter((item) => {
          return item.unidade_nome === this.filtro.unidade_solicitante;
        });
      }

      // Filtrar por data de
      if (this.filtro.data_de) {
        dados = dados.filter((item) => {
          const dataPedido = new Date(item.data_pedido)
            .toISOString()
            .split("T")[0];
          const dataFiltro = this.filtro.data_de;
          return dataPedido >= dataFiltro;
        });
      }

      // Filtrar por data até
      if (this.filtro.data_ate) {
        dados = dados.filter((item) => {
          const dataPedido = new Date(item.data_pedido)
            .toISOString()
            .split("T")[0];
          const dataFiltro = this.filtro.data_ate;
          return dataPedido <= dataFiltro;
        });
      }

      return dados;
    },

    // Computed properties para paginação
    totalPaginas() {
      return Math.ceil(this.dadosFiltrados.length / this.registrosPorPagina);
    },

    dadosPaginados() {
      const inicio = (this.paginaAtual - 1) * this.registrosPorPagina;
      const fim = inicio + this.registrosPorPagina;
      return this.dadosFiltrados.slice(inicio, fim);
    },
  },
  watch: {
    // Resetar página quando filtros mudarem
    filtro: {
      handler() {
        this.paginaAtual = 1;
      },
      deep: true,
    },
    // Resetar página quando mudar registros por página
    registrosPorPagina() {
      this.paginaAtual = 1;
    },
  },
  methods: {
    onSearch(search, loading) {
      if (search.length < 3) {
        this.materiaisFiltrados = [];
        return;
      }

      // Debounce para evitar muitas requisições
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.searchMateriais(search, loading);
      }, 300);
    },

    async searchMateriais(search, loading) {
      if (typeof loading === "function") loading(true);
      this.loadingMateriais = true;

      try {
        const api = this.$store.state.api + "materiais";
        const params = {
          paginated: true,
          limit: 20,
          query: search,
        };

        const response = await axios.get(api, { params });

        console.log("Response da API:", response.data);

        if (response.data && response.data.data) {
          this.materiaisFiltrados = response.data.data;
        } else {
          this.materiaisFiltrados = [];
        }
      } catch (error) {
        console.error("Erro ao buscar materiais:", error);
        this.$message(
          "Erro",
          "Erro ao buscar materiais. Tente novamente.",
          "error"
        );
        this.materiaisFiltrados = [];
      } finally {
        if (typeof loading === "function") loading(false);
        this.loadingMateriais = false;
      }
    },

    onMaterialSelected(material) {
      console.log("Material selecionado:", material);
      // Limpar resultados da pesquisa anterior quando um novo material é selecionado
      this.unidadesSolicitantes = [];
      this.pesquisaRealizada = false;
    },

    // Limpar busca
    clearSearch() {
      this.materialSelecionado = null;
      this.materiaisFiltrados = [];
      this.unidadesSolicitantes = [];
      this.pesquisaRealizada = false;
    },

    // Pesquisar unidades que solicitaram o material
    async pesquisarUnidades() {
      if (!this.materialSelecionado || !this.materialSelecionado.id) {
        this.$message("Erro", "Nenhum material selecionado.", "error");
        return;
      }

      this.loadingUnidades = true;
      this.pesquisaRealizada = false;
      this.paginaAtual = 1;

      try {
        // Buscar pedidos que contêm este material (COM STATUS CORRETO!)
        const api = this.$store.state.api + "material_pedidos_com_unidades";
        const params = {
          paginated: true,
          limit: 50000,
          material_id: this.materialSelecionado.id,
        };

        const response = await axios.get(api, { params });

        console.log("Response material_pedidos:", response.data);

        if (response.data && response.data.data) {
          // Processar os dados já com as unidades incluídas
          this.unidadesSolicitantes = response.data.data.map(
            (materialPedido) => ({
              pedido_id: materialPedido.pedido_id,
              material_pedido_id: materialPedido.id,
              quantidade_solicitada: materialPedido.quantidade_solicitada,
              quantidade_atendida: materialPedido.quantidade_atendida || 0,
              baixa_estoque: materialPedido.baixa_estoque || 0,
              data_pedido:
                materialPedido.pedido?.created_at || materialPedido.created_at,
              status: materialPedido.pedido ? materialPedido.pedido.status : 0,
              unidade_nome:
                materialPedido.pedido?.u_solicitante?.nome || "Não informado",
              data_inicio: materialPedido.pedido?.data_inicio || null,
              data_fim: materialPedido.pedido?.data_fim || null,
              suplementar: materialPedido.pedido?.suplementar || false,
              rapido: materialPedido.pedido?.rapido || false,
            })
          );

          // Ordenar por data mais recente primeiro
          this.unidadesSolicitantes = this.unidadesSolicitantes.sort(
            (a, b) => new Date(b.data_pedido) - new Date(a.data_pedido)
          );

          // Buscar unidades dos resultados para o filtro
          this.buscarUnidades();
        } else {
          this.unidadesSolicitantes = [];
        }

        this.pesquisaRealizada = true;
      } catch (error) {
        console.error("Erro ao buscar unidades:", error);
        this.$message(
          "Erro",
          "Erro ao buscar unidades que solicitaram o material. Tente novamente.",
          "error"
        );
        this.unidadesSolicitantes = [];
        this.pesquisaRealizada = true;
      } finally {
        this.loadingUnidades = false;
      }
    },

    filtrar() {
      // Método vazio por enquanto - os filtros funcionam via computed
      console.log("Filtros aplicados:", this.filtro);
    },

    irParaPagina(pagina) {
      if (pagina >= 1 && pagina <= this.totalPaginas) {
        this.paginaAtual = pagina;
      }
    },

    limparFiltros() {
      this.filtro = [];
    },

    async buscarUnidades() {
      // Extrair unidades únicas dos resultados da pesquisa
      if (this.unidadesSolicitantes.length > 0) {
        const unidadesUnicas = [];
        const nomesVistos = new Set();

        this.unidadesSolicitantes.forEach((item) => {
          if (item.unidade_nome && !nomesVistos.has(item.unidade_nome)) {
            nomesVistos.add(item.unidade_nome);
            unidadesUnicas.push({
              id: item.unidade_nome, // usar nome como ID para filtro
              name: item.unidade_nome,
            });
          }
        });

        this.unidades = unidadesUnicas.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      }
    },

    exportarParaExcel() {
      // Usar os dados filtrados que estão sendo exibidos na tabela
      const dadosParaExportar = this.dadosFiltrados;

      if (dadosParaExportar.length === 0) {
        this.$message("Aviso", "Não há dados para exportar.", "warning");
        return;
      }

      // Criar JSON com os dados filtrados
      const jsonData = dadosParaExportar.map((item) => ({
        "ID Pedido": item.pedido_id,
        "Unidade Solicitante": item.unidade_nome || "Não informado",
        "Data do Pedido": this.formatarData(item.data_pedido),
        "Quantidade Solicitada": item.quantidade_solicitada,
        "Quantidade Atendida": item.quantidade_atendida || 0,
        "Baixa de Estoque": item.baixa_estoque || 0,
        "Status do Pedido": this.getStatusText(item.status),
        Tipo: this.getTipoPedido(item),
        Material: this.materialSelecionado
          ? this.materialSelecionado.nome
          : "N/A",
      }));

      // Criar e baixar arquivo Excel
      this.downloadExcel(
        jsonData,
        `Pesquisa_Material_${this.materialSelecionado?.nome || "Material"}_${
          new Date().toISOString().split("T")[0]
        }`
      );
    },

    downloadExcel(data, filename) {
      // Criar cabeçalho
      const headers = Object.keys(data[0]);

      // Criar linhas de dados
      const rows = data.map((row) => Object.values(row));

      // Função para limpar e formatar células com largura automática
      const formatCell = (cell, columnIndex) => {
        if (cell === null || cell === undefined) return "";

        let cellStr = String(cell).trim();

        // Calcular largura necessária baseada no conteúdo da coluna
        const header = headers[columnIndex];
        const headerLength = header ? header.length : 0;
        const cellLength = cellStr.length;

        // Usar o maior entre header e conteúdo, com padding mínimo
        const targetWidth = Math.max(headerLength, cellLength) + 2;

        // Adicionar espaços para ajustar largura se necessário
        if (cellLength < targetWidth) {
          const paddingNeeded = targetWidth - cellLength;
          const leftPadding = Math.floor(paddingNeeded / 2);
          const rightPadding = paddingNeeded - leftPadding;
          cellStr = `"${" ".repeat(leftPadding)}${cellStr}${" ".repeat(
            rightPadding
          )}"`;
        } else {
          // Se contém ponto e vírgula ou aspas, colocar entre aspas
          if (cellStr.includes(";") || cellStr.includes('"')) {
            cellStr = `"${cellStr.replace(/"/g, '""')}"`;
          }
        }

        return cellStr;
      }; // Usar ponto e vírgula (padrão brasileiro do Excel)
      const separator = ";";

      // Criar CSV limpo
      let csvContent = "";

      // Adicionar cabeçalho
      csvContent +=
        headers.map((header) => formatCell(header)).join(separator) + "\r\n";

      // Adicionar dados
      rows.forEach((row) => {
        const formattedRow = row.map((cell, index) => {
          return formatCell(cell, index);
        });
        csvContent += formattedRow.join(separator) + "\r\n";
      });

      // Criar arquivo XLS (Excel nativo) com BOM UTF-8
      const blob = new Blob(["\ufeff" + csvContent], {
        type: "application/vnd.ms-excel;charset=utf-8;",
      });

      // Fazer download como .xls (Excel nativo)
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `${filename}.xls`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, // Formatar data
    formatarData(data) {
      if (!data) return "Não informado";
      return new Date(data).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    // Get status variant for badge color
    getStatusVariant(status) {
      const variants = {
        0: "primary", // Solicitado
        1: "danger", // Rejeitado
        2: "secondary", // Aguardando Retirada
        3: "warning", // Em Transporte
        4: "info", // Recebido
        5: "dark", // Finalizado
        6: "danger", // Cancelado
        7: "warning", // Recebido Parcialmente
        8: "warning", // Em Separação
        9: "success", // Aprovado
        10: "secondary", // Pendente Baixa de Estoque
        11: "primary", // Autorizado
        12: "success", // Entregue
        13: "danger", // Não Entregue
      };
      return variants[status] || "dark";
    },

    // Get status text
    getStatusText(status) {
      const statusText = {
        0: "Solicitado",
        1: "Rejeitado",
        2: "Aguardando Retirada",
        3: "Em Transporte",
        4: "Recebido",
        5: "Finalizado",
        6: "Cancelado",
        7: "Recebido Parcialmente",
        8: "Em Separação",
        9: "Aprovado",
        10: "Pendente Baixa de Estoque",
        11: "Autorizado",
        12: "Entregue",
        13: "Não Entregue",
      };
      return statusText[status] || "Não informado";
    },

    getTipoPedido(item) {
      if (item.data_inicio && item.data_fim) return "Pedido semanal";
      if (item.suplementar) return "Pedido Suplementar";
      if (item.rapido) return "Pedido Rápido";
      return "Pedido SOS";
    },

    salvarFiltroAtual() {
      if (this.materialSelecionado) {
        const filtroAtual = {
          material: this.materialSelecionado,
          resultados: this.unidadesSolicitantes,
        };
        localStorage.setItem(
          "pesquisaMaterialFiltro",
          JSON.stringify(filtroAtual)
        );
      }
    },
  },
  mounted() {
    // Recuperar filtro salvo se existir
    const filtroSalvo = localStorage.getItem("pesquisaMaterialFiltro");
    if (filtroSalvo) {
      try {
        const filtro = JSON.parse(filtroSalvo);
        if (filtro.material) {
          this.materialSelecionado = filtro.material;
          this.unidadesSolicitantes = filtro.resultados || [];
        }
      } catch (error) {
        console.log("Erro ao recuperar filtro:", error);
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    // Salvar filtro antes de sair da tela
    this.salvarFiltroAtual();
    next();
  },
};
</script>

<style scoped>
.material-info {
  background-color: #f8f9fa;
  border-left: 4px solid #007bff;
}

.vselect {
  font-size: 1rem;
}

.vs__dropdown-toggle {
  border: 1px solid #d1d3e2;
  border-radius: 0.35rem;
  padding: 0.375rem 0.75rem;
}

/* Estilo da barra de pesquisa - texto mais escuro */
.vs__dropdown-toggle input {
  color: #2c3e50 !important;
  font-weight: 500;
}

.vs__selected {
  color: #2c3e50 !important;
  font-weight: 500;
}

.vs__search::placeholder {
  color: #6c757d !important;
}

/* Responsividade para modo dark */
.table-dark-responsive {
  background-color: transparent;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

/* Modo dark - múltiplos seletores para compatibilidade */
.dark-layout .table-dark-responsive,
[data-theme="dark"] .table-dark-responsive,
.theme-dark .table-dark-responsive,
body.dark .table-dark-responsive {
  background-color: #283046 !important;
  color: #b4b7bd !important;
}

.dark-layout .table-dark-responsive th,
[data-theme="dark"] .table-dark-responsive th,
.theme-dark .table-dark-responsive th,
body.dark .table-dark-responsive th {
  background-color: #283046 !important;
  color: #b4b7bd !important;
  border-color: #3b4253 !important;
}

.dark-layout .table-dark-responsive td,
[data-theme="dark"] .table-dark-responsive td,
.theme-dark .table-dark-responsive td,
body.dark .table-dark-responsive td {
  background-color: #283046 !important;
  color: #b4b7bd !important;
  border-color: #3b4253 !important;
}

.dark-layout .table-dark-responsive tbody tr:hover,
[data-theme="dark"] .table-dark-responsive tbody tr:hover,
.theme-dark .table-dark-responsive tbody tr:hover,
body.dark .table-dark-responsive tbody tr:hover {
  background-color: #3b4253 !important;
}

.dark-layout .material-info,
[data-theme="dark"] .material-info,
.theme-dark .material-info,
body.dark .material-info {
  background-color: #283046 !important;
  color: #b4b7bd !important;
  border-left: 4px solid #7367f0 !important;
}

/* Barra de pesquisa no modo dark */
.dark-layout .vs__dropdown-toggle,
[data-theme="dark"] .vs__dropdown-toggle,
.theme-dark .vs__dropdown-toggle,
body.dark .vs__dropdown-toggle {
  background-color: #283046 !important;
  border-color: #3b4253 !important;
}

.dark-layout .vs__dropdown-toggle input,
[data-theme="dark"] .vs__dropdown-toggle input,
.theme-dark .vs__dropdown-toggle input,
body.dark .vs__dropdown-toggle input {
  color: #b4b7bd !important;
}

.dark-layout .vs__selected,
[data-theme="dark"] .vs__selected,
.theme-dark .vs__selected,
body.dark .vs__selected {
  color: #b4b7bd !important;
}

.dark-layout .vs__search::placeholder,
[data-theme="dark"] .vs__search::placeholder,
.theme-dark .vs__search::placeholder,
body.dark .vs__search::placeholder {
  color: #6c757d !important;
}

/* Bordinha da tabela no modo dark */
.dark-layout .table-dark-responsive,
[data-theme="dark"] .table-dark-responsive,
.theme-dark .table-dark-responsive,
body.dark .table-dark-responsive {
  border: 1px solid #3b4253 !important;
}

/* Botão Ver Pedido - fundo branco no modo dark */
.btn-ver-pedido {
  background-color: #fff !important;
  border-color: #007bff !important;
  color: #007bff !important;
}

.btn-ver-pedido:hover {
  background-color: #007bff !important;
  border-color: #007bff !important;
  color: #fff !important;
}

/* Modo dark - manter fundo branco */
.dark-layout .btn-ver-pedido,
[data-theme="dark"] .btn-ver-pedido,
.theme-dark .btn-ver-pedido,
body.dark .btn-ver-pedido {
  background-color: #fff !important;
  border-color: #007bff !important;
  color: #007bff !important;
}

.dark-layout .btn-ver-pedido:hover,
[data-theme="dark"] .btn-ver-pedido:hover,
.theme-dark .btn-ver-pedido:hover,
body.dark .btn-ver-pedido:hover {
  background-color: #007bff !important;
  border-color: #007bff !important;
  color: #fff !important;
}
</style>